import { useState } from 'react';
import { Button, Input, Label, SectionCard } from '@/components/ui';
import { RadioGroup } from '@/components/ui';
import { useTranslation } from '@/lib/i18n';
import type {
  CouponDefinition,
  CouponDiscountType,
  CouponUpdateRequest,
} from '@/lib/api-admin';
import { ProductMultiSelectModalField } from '@/features/catalog/components/ProductMultiSelectModalField';
import { useCouponUpdateMutation } from '../hooks/useCouponMutation';
import { CouponFormSection as FormSection } from './CouponFormSection';

const DISCOUNT_TYPE_OPTIONS = [
  { value: 'FIXED', label: '정액 (원)' },
  { value: 'PERCENT', label: '정률 (%)' },
] as const;

const ACTIVE_OPTIONS = [
  { value: 'on', label: '사용' },
  { value: 'off', label: '정지' },
] as const;

interface Props {
  coupon: CouponDefinition;
  onSuccess?: () => void;
  onCancel?: () => void;
}

/**
 * 쿠폰 수정 폼. BE `PUT /api/admin/v1/coupons/{couponId}` (CouponDefinitionUpdateRequest →
 * CouponDefinition.update RAW-ASSIGN)는 **요청의 모든 필드를 무조건 반영**한다 — 누락 필드는
 * null 로 덮어써 "무제한" 으로 초기화된다. ★[coupon-audit HIGH] 종전 폼은 maxOrderAmount/
 * maxDiscountAmount/budgetLimit 을 노출하지 않은 채 생략해, 제목만 고쳐 저장해도 정률 cap·
 * 예산 한도가 조용히 삭제됐다(무제한 할인화). 이제 전부 노출·round-trip 한다(비우면 무제한
 * = 의도 표시). 적용범위(applicableProductIds)도 PRODUCT 쿠폰에서 편집(null=미변경 BE 계약,
 * CART 는 미전송).
 *
 * 쿠폰코드(code)는 update DTO 에 없어 변경 불가(표시만). 쿠폰종류(couponType)는 BE 가 반영은
 * 하지만 CART↔PRODUCT 전환은 적용범위 의미가 뒤바뀌어 의도적으로 잠근다(기존 값 전송).
 */
export const CouponEditForm = ({ coupon, onSuccess, onCancel }: Props) => {
  const { t } = useTranslation('common');
  const mutation = useCouponUpdateMutation();

  const [badge, setBadge] = useState(coupon.badge ?? '');
  const [title, setTitle] = useState(coupon.title ?? '');
  const [active, setActive] = useState(coupon.active !== false);
  const [description, setDescription] = useState(coupon.description ?? '');
  const [discountType, setDiscountType] = useState<CouponDiscountType>(coupon.discountType);
  const [discountAmount, setDiscountAmount] = useState(String(coupon.discountAmount ?? ''));
  const [conditionAmount, setConditionAmount] = useState(String(coupon.conditionAmount ?? 0));
  // [coupon-audit] BE raw-assign 대상 — 미노출 시 저장마다 무제한으로 초기화되던 3필드.
  const [maxOrderAmount, setMaxOrderAmount] = useState(
    coupon.maxOrderAmount != null ? String(coupon.maxOrderAmount) : '',
  );
  const [maxDiscountAmount, setMaxDiscountAmount] = useState(
    coupon.maxDiscountAmount != null ? String(coupon.maxDiscountAmount) : '',
  );
  const [budgetLimit, setBudgetLimit] = useState(
    coupon.budgetLimit != null && coupon.budgetLimit !== 0 ? String(coupon.budgetLimit) : '',
  );
  const [maxQuantity, setMaxQuantity] = useState(
    coupon.maxQuantity != null ? String(coupon.maxQuantity) : '',
  );
  const [validFrom, setValidFrom] = useState(toLocalInput(coupon.validFrom));
  const [validTo, setValidTo] = useState(toLocalInput(coupon.validTo));
  const [applicableProductIds, setApplicableProductIds] = useState<string[]>(
    coupon.applicableProductIds ?? [],
  );
  const [error, setError] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!badge.trim() || !title.trim()) {
      setError(t('coupons.editValidation', '스티커와 제목은 필수입니다.'));
      return;
    }
    if (discountType === 'PERCENT' && (Number(discountAmount) <= 0 || Number(discountAmount) > 100)) {
      setError(t('coupons.percentRange', '정률 할인은 1~100% 사이여야 합니다.'));
      return;
    }
    if (coupon.couponType === 'PRODUCT' && applicableProductIds.length === 0) {
      setError(t('coupons.scopeRequired', '상품 쿠폰은 적용 범위 상품을 1개 이상 선택해야 합니다.'));
      return;
    }
    const body: CouponUpdateRequest = {
      // couponType 은 의도적 잠금 — 기존 값을 그대로 전달(BE 는 반영하지만 폼에서 전환 미허용).
      couponType: coupon.couponType,
      badge: badge.trim(),
      title: title.trim(),
      ...(description.trim() ? { description: description.trim() } : {}),
      discountType,
      discountAmount: Number(discountAmount) || 0,
      conditionAmount: Number(conditionAmount) || 0,
      // BE raw-assign — 항상 전송. 빈 입력 = null = 무제한(폼에 보이는 의도적 clear).
      maxOrderAmount: maxOrderAmount ? Number(maxOrderAmount) : null,
      maxDiscountAmount: maxDiscountAmount ? Number(maxDiscountAmount) : null,
      budgetLimit: budgetLimit ? Number(budgetLimit) : null,
      ...(maxQuantity ? { maxQuantity: Number(maxQuantity) } : {}),
      ...(validFrom ? { validFrom: new Date(validFrom).toISOString() } : {}),
      ...(validTo ? { validTo: new Date(validTo).toISOString() } : {}),
      // 적용범위 — PRODUCT 만 교체 전송(≥1 가드 통과 후), CART 는 미전송(null=미변경 BE 계약).
      ...(coupon.couponType === 'PRODUCT' ? { applicableProductIds } : {}),
      active,
    };
    mutation.mutate(
      { couponId: coupon.id, body },
      {
        onSuccess: () => onSuccess?.(),
        onError: (err) => setError(err.message),
      },
    );
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="coupon-edit-form">
      <SectionCard title={t('coupons.editTitle', '쿠폰 수정')}>
        <div className="space-y-6">
          <FormSection label={t('coupons.cols.code', '쿠폰코드')}>
            <div className="flex flex-wrap items-center gap-2">
              <span
                className="font-mono text-sm"
                style={{ color: 'var(--content-muted)' }}
                data-testid="coupon-edit-code"
              >
                {coupon.code}
              </span>
              <span className="text-xs" style={{ color: 'var(--content-muted)' }}>
                {t('coupons.editCodeReadonly', '(쿠폰코드·쿠폰종류는 변경할 수 없습니다)')}
              </span>
            </div>
          </FormSection>

          <FormSection label={t('coupons.editSectionBasic', '기본 정보')}>
            <div className="space-y-4">
              {/* QA-412 — 정지하면 관리자 발급·고객 다운로드·사용이 막힌다(이미 받은 쿠폰도 사용 불가). */}
              <div className="space-y-1.5">
                <Label id="coupon-edit-active-label">{t('coupons.cols.active', '사용 여부')}</Label>
                <RadioGroup
                  value={active ? 'on' : 'off'}
                  onValueChange={(v) => setActive(v === 'on')}
                  options={ACTIVE_OPTIONS}
                  orientation="horizontal"
                  aria-labelledby="coupon-edit-active-label"
                  className="min-h-8 items-center"
                  data-testid="coupon-edit-active"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="coupon-edit-badge" required>{t('coupons.cols.badge', '스티커')}</Label>
                  <Input
                    id="coupon-edit-badge"
                    value={badge}
                    onChange={(e) => setBadge(e.target.value)}
                    maxLength={50}
                    required
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="coupon-edit-title" required>{t('coupons.cols.title', '제목')}</Label>
                  <Input
                    id="coupon-edit-title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    maxLength={255}
                    required
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="coupon-edit-desc">{t('coupons.editDescription', '설명')}</Label>
                <Input
                  id="coupon-edit-desc"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={t('coupons.editOptional', '(선택)')}
                  maxLength={1000}
                />
              </div>
            </div>
          </FormSection>

          <FormSection label={t('coupons.editSectionDiscount', '할인 조건')}>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label id="coupon-edit-discount-type-label">
                    {t('coupons.cols.discountType', '할인유형')}
                  </Label>
                  <RadioGroup
                    value={discountType}
                    onValueChange={(v) => setDiscountType(v as CouponDiscountType)}
                    options={DISCOUNT_TYPE_OPTIONS}
                    orientation="horizontal"
                    aria-labelledby="coupon-edit-discount-type-label"
                    className="min-h-8 items-center"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="coupon-edit-discount-amount" required>
                    {t('coupons.editDiscountAmount', '할인 금액/율')}
                  </Label>
                  <Input
                    id="coupon-edit-discount-amount"
                    type="number"
                    min="0"
                    value={discountAmount}
                    onChange={(e) => setDiscountAmount(e.target.value)}
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="coupon-edit-condition">
                    {t('coupons.editConditionAmount', '최소 주문금액')}
                  </Label>
                  <Input
                    id="coupon-edit-condition"
                    type="number"
                    min="0"
                    value={conditionAmount}
                    onChange={(e) => setConditionAmount(e.target.value)}
                    placeholder="0"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="coupon-edit-max-order">
                    {t('coupons.editMaxOrderAmount', '최대 주문금액')}
                  </Label>
                  <Input
                    id="coupon-edit-max-order"
                    type="number"
                    min="0"
                    value={maxOrderAmount}
                    onChange={(e) => setMaxOrderAmount(e.target.value)}
                    placeholder={t('coupons.editUnlimited', '무제한')}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="coupon-edit-max-discount">
                    {t('coupons.editMaxDiscountAmount', '최대 할인금액')}
                  </Label>
                  <Input
                    id="coupon-edit-max-discount"
                    type="number"
                    min="0"
                    value={maxDiscountAmount}
                    onChange={(e) => setMaxDiscountAmount(e.target.value)}
                    placeholder={t('coupons.editPercentCap', '정률 할인 cap (무제한)')}
                  />
                </div>
              </div>
            </div>
          </FormSection>

          <FormSection label={t('coupons.editSectionBudget', '쿠폰 예산')}>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="coupon-edit-budget-limit">
                  {t('coupons.editBudgetLimit', '예산 한도')}
                </Label>
                <Input
                  id="coupon-edit-budget-limit"
                  type="number"
                  min="0"
                  value={budgetLimit}
                  onChange={(e) => setBudgetLimit(e.target.value)}
                  placeholder={t('coupons.editBudgetUnlimited', '0 또는 미입력 시 무제한')}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="coupon-edit-budget-spent">
                  {t('coupons.editBudgetSpent', '예산 사용액')}
                </Label>
                <Input
                  id="coupon-edit-budget-spent"
                  type="text"
                  value={(coupon.budgetSpent ?? 0).toLocaleString('ko-KR')}
                  readOnly
                  disabled
                  aria-label={t('coupons.editBudgetSpentAria', '예산 사용액 (자동 누적)')}
                />
              </div>
            </div>
          </FormSection>

          <FormSection label={t('coupons.editSectionIssue', '발급 설정')}>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="coupon-edit-max-qty">{t('coupons.editMaxQuantity', '최대 발급 수')}</Label>
                <Input
                  id="coupon-edit-max-qty"
                  type="number"
                  min="1"
                  value={maxQuantity}
                  onChange={(e) => setMaxQuantity(e.target.value)}
                  placeholder={t('coupons.editUnlimited', '무제한')}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="coupon-edit-valid-from">{t('coupons.editValidFrom', '유효 시작일')}</Label>
                <Input
                  id="coupon-edit-valid-from"
                  type="datetime-local"
                  value={validFrom}
                  onChange={(e) => setValidFrom(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="coupon-edit-valid-to">{t('coupons.editValidTo', '유효 종료일')}</Label>
                <Input
                  id="coupon-edit-valid-to"
                  type="datetime-local"
                  value={validTo}
                  onChange={(e) => setValidTo(e.target.value)}
                />
              </div>
            </div>
          </FormSection>

          {coupon.couponType === 'PRODUCT' ? (
            <FormSection label={t('coupons.editSectionScope', '적용 범위')}>
              <ProductMultiSelectModalField
                value={applicableProductIds}
                onChange={setApplicableProductIds}
                emptyHint={t('coupons.scopeEmptyHint', '적용할 상품을 선택하세요 (상품 쿠폰 필수)')}
              />
            </FormSection>
          ) : null}

          {error && (
            <p role="alert" className="text-sm" style={{ color: 'var(--status-danger-fg)' }}>
              {error}
            </p>
          )}

          <div
            className="flex justify-end gap-2 border-t pt-4"
            style={{ borderColor: 'var(--divider-soft)' }}
          >
            {onCancel && (
              <Button type="button" variant="ghost" size="md" onClick={onCancel}>
                {t('coupons.cancel', '취소')}
              </Button>
            )}
            <Button type="submit" variant="cta" size="md" disabled={mutation.isPending}>
              {mutation.isPending ? t('coupons.editSaving', '저장 중...') : t('coupons.editSave', '저장')}
            </Button>
          </div>
        </div>
      </SectionCard>
    </form>
  );
};

/** ISO(UTC) → `<input type="datetime-local">` 가 받는 local `yyyy-MM-ddThh:mm` 문자열. */
const toLocalInput = (iso?: string): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
